import * as THREE from 'three';
import { EFFECT_LAYER } from './enhanced-effects';

// ---------------------------------------------------------------------------
// Generic object pool + specialised mesh / light pools
// ---------------------------------------------------------------------------

/** Generic pool: pre-creates objects, hands them out, and recycles them. */
export class Pool<T> {
  protected available: T[] = [];
  protected inUse: Set<T> = new Set();
  private factory: () => T;
  private reset: (item: T) => void;

  constructor(size: number, factory: () => T, reset: (item: T) => void) {
    this.factory = factory;
    this.reset = reset;
    for (let i = 0; i < size; i++) {
      const item = factory();
      reset(item);
      this.available.push(item);
    }
  }

  /** Get an item from the pool. Returns null if exhausted. */
  acquire(): T {
    let item = this.available.pop();
    if (!item) {
      // Pool exhausted — grow by one
      item = this.factory();
    }
    this.inUse.add(item);
    return item;
  }

  /** Return an item to the pool. */
  release(item: T): void {
    if (!this.inUse.has(item)) return;
    this.inUse.delete(item);
    this.reset(item);
    this.available.push(item);
  }

  /** Number of items currently available. */
  get freeCount(): number {
    return this.available.length;
  }

  /** Number of items currently in use. */
  get activeCount(): number {
    return this.inUse.size;
  }
}

// ---------------------------------------------------------------------------
// MeshPool — pool of THREE.Mesh with visibility toggling
// ---------------------------------------------------------------------------

export class MeshPool extends Pool<THREE.Mesh> {
  readonly parent: THREE.Object3D;

  constructor(
    size: number,
    geometry: THREE.BufferGeometry,
    material: THREE.Material,
    parent: THREE.Object3D,
  ) {
    super(
      size,
      () => {
        const mesh = new THREE.Mesh(geometry, material.clone());
        mesh.visible = false;
        mesh.layers.set(EFFECT_LAYER); // keep off layer 0 so raycaster skips
        parent.add(mesh);
        return mesh;
      },
      (mesh) => {
        mesh.visible = false;
        mesh.scale.set(1, 1, 1);
      },
    );
    this.parent = parent;
  }
}

// ---------------------------------------------------------------------------
// Light pool — pool of THREE.PointLight with visibility toggling
// ---------------------------------------------------------------------------

export class LightPool extends Pool<THREE.PointLight> {
  readonly parent: THREE.Object3D;

  constructor(
    size: number,
    color: THREE.ColorRepresentation,
    intensity: number,
    distance: number,
    parent: THREE.Object3D,
  ) {
    super(
      size,
      () => {
        const light = new THREE.PointLight(color, intensity, distance);
        light.visible = false;
        light.layers.set(EFFECT_LAYER); // keep off layer 0 so raycaster skips
        parent.add(light);
        return light;
      },
      (light) => {
        light.visible = false;
      },
    );
    this.parent = parent;
  }

  /** Detach all pooled lights from the scene and dispose them. */
  dispose(): void {
    const all = [...this.available, ...this.inUse];
    for (const light of all) {
      this.parent.remove(light);
      light.dispose();
    }
    this.available.length = 0;
    this.inUse.clear();
  }
}

// ---------------------------------------------------------------------------
// Pre-configured pools (call initPools after scene is created)
// ---------------------------------------------------------------------------

let muzzleFlashPool: LightPool | null = null;

export function initPools(scene: THREE.Scene): void {
  // Muzzle flashes: small orange point lights
  muzzleFlashPool = new LightPool(10, 0xff8800, 2.0, 8, scene);
}

export function getMuzzleFlashPool(): LightPool {
  if (!muzzleFlashPool) throw new Error('Pools not initialised — call initPools first');
  return muzzleFlashPool;
}

export function disposePools(): void {
  if (muzzleFlashPool) {
    muzzleFlashPool.dispose();
    muzzleFlashPool = null;
  }
}
